import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orderAPI } from '../../utils/api';
import { Eye, X } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUSES = ['pending','processing','shipped','delivered','cancelled','refunded'];
const STATUS_ALB = { pending:'Në pritje',processing:'Duke u procesuar',shipped:'Dërguar',delivered:'Dorëzuar',cancelled:'Anuluar',refunded:'Rimbursuar' };
const BADGE_MAP = { pending:'badge-gold',processing:'badge-blue',shipped:'badge-blue',delivered:'badge-green',cancelled:'badge-red',refunded:'badge-gray' };

export default function AdminOrdersPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [tracking, setTracking] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-orders', status, page],
    queryFn: () => orderAPI.adminGetAll({ status: status || undefined, page }).then(r => r.data),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, status, tracking_number }) => orderAPI.updateStatus(id, { status, tracking_number }),
    onSuccess: () => { toast.success('Statusi u përditësua!'); qc.invalidateQueries(['admin-orders']); setSelectedOrder(null); },
    onError: (e) => toast.error(e.response?.data?.error || 'Gabim'),
  });

  return (
    <div>
      <div className="page-top">
        <div>
          <h1 style={{ fontSize:'1.6rem',marginBottom:4 }}>Porositë</h1>
          <p style={{ color:'var(--text-light)',fontSize:'.88rem' }}>{data?.pagination?.total || 0} porosi gjithsej</p>
        </div>
      </div>

      {/* Status filter */}
      <div className="status-filters">
        <button className={`status-filter${!status ? ' active' : ''}`} onClick={() => { setStatus(''); setPage(1); }}>Të gjitha</button>
        {STATUSES.map(s => (
          <button key={s} className={`status-filter${status === s ? ' active' : ''}`} onClick={() => { setStatus(s); setPage(1); }}>
            {STATUS_ALB[s]}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding:0,overflow:'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Nr. Porosisë</th><th>Klienti</th><th>Data</th>
              <th>Totali</th><th>Pagesa</th><th>Statusi</th><th>Veprime</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} style={{ textAlign:'center',padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>}
              {data?.orders?.map(o => (
                <tr key={o.id}>
                  <td><code style={{ fontSize:'.8rem',background:'var(--cream)',padding:'2px 8px',borderRadius:4 }}>{o.order_number}</code></td>
                  <td>
                    <div style={{ fontWeight:500,fontSize:'.88rem' }}>{o.user_name || 'Guest'}</div>
                    <div style={{ fontSize:'.75rem',color:'var(--text-light)' }}>{o.user_email || o.guest_email}</div>
                  </td>
                  <td style={{ fontSize:'.82rem' }}>{new Date(o.created_at).toLocaleDateString('sq')}</td>
                  <td style={{ fontWeight:700 }}>${(+o.total).toFixed(2)}</td>
                  <td><span className={`badge ${o.payment_status === 'paid' ? 'badge-green' : 'badge-gold'}`}>{o.payment_status === 'paid' ? 'Paguar' : 'Pa paguar'}</span></td>
                  <td><span className={`badge ${BADGE_MAP[o.status]}`}>{STATUS_ALB[o.status]}</span></td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedOrder(o); setNewStatus(o.status); setTracking(o.tracking_number||''); }}>
                      <Eye size={14} /> Shiko
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data?.pagination?.pages > 1 && (
        <div style={{ display:'flex',justifyContent:'center',gap:8,marginTop:20 }}>
          {Array.from({ length: data.pagination.pages }, (_,i) => (
            <button key={i} className={`btn btn-sm ${page === i+1 ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPage(i+1)}>{i+1}</button>
          ))}
        </div>
      )}

      {/* Order detail modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedOrder(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2 style={{ fontSize:'1.1rem' }}>Porosia {selectedOrder.order_number}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedOrder(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20 }}>
                <div className="card" style={{ padding:16 }}>
                  <div style={{ fontSize:'.75rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--text-light)',marginBottom:10 }}>Adresa e Dërgimit</div>
                  {(() => { try { const a = typeof selectedOrder.shipping_address === 'string' ? JSON.parse(selectedOrder.shipping_address) : selectedOrder.shipping_address; return (<div style={{ fontSize:'.88rem' }}><div>{a?.full_name}</div><div>{a?.street}</div><div>{a?.city}, {a?.country}</div></div>); } catch { return <div>N/A</div>; } })()}
                </div>
                <div className="card" style={{ padding:16 }}>
                  <div style={{ fontSize:'.75rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--text-light)',marginBottom:10 }}>Detajet</div>
                  <div style={{ fontSize:'.88rem',display:'flex',flexDirection:'column',gap:6 }}>
                    <div>Nëntotali: <strong>${(+selectedOrder.subtotal).toFixed(2)}</strong></div>
                    {selectedOrder.discount > 0 && <div style={{ color:'var(--success)' }}>Zbritje: -${(+selectedOrder.discount).toFixed(2)}</div>}
                    <div>Dërgim: <strong>${(+selectedOrder.shipping_cost).toFixed(2)}</strong></div>
                    <div style={{ fontWeight:700,fontSize:'1rem' }}>Totali: ${(+selectedOrder.total).toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Ndrysho Statusin</label>
                <select className="form-input" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_ALB[s]}</option>)}
                </select>
              </div>
              {(newStatus === 'shipped' || newStatus === 'delivered') && (
                <div className="form-group">
                  <label className="form-label">Nr. i Gjurmimit</label>
                  <input className="form-input" value={tracking} onChange={e => setTracking(e.target.value)} placeholder="p.sh. TRK123456789" />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setSelectedOrder(null)}>Mbyll</button>
              <button className="btn btn-primary" disabled={updateMut.isPending}
                onClick={() => updateMut.mutate({ id: selectedOrder.id, status: newStatus, tracking_number: tracking })}>
                {updateMut.isPending ? 'Duke ruajtur...' : 'Ruaj Ndryshimet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .page-top { display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;flex-wrap:wrap;gap:16px; }
        .status-filters { display:flex;gap:8px;flex-wrap:wrap;margin-bottom:20px; }
        .status-filter { padding:7px 16px;border-radius:var(--radius-full);border:1.5px solid var(--cream-dark);background:white;color:var(--text-mid);font-size:.8rem;font-weight:500;cursor:pointer;transition:all .2s; }
        .status-filter.active,.status-filter:hover { background:var(--green-deep);color:white;border-color:var(--green-deep); }
        .modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px); }
        .modal-box { background:white;border-radius:var(--radius-xl);width:min(620px,100%);max-height:90vh;display:flex;flex-direction:column;animation:modalIn .2s ease; }
        @keyframes modalIn { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
        .modal-header { display:flex;justify-content:space-between;align-items:center;padding:20px 24px;border-bottom:1px solid var(--cream-dark); }
        .modal-body { padding:24px;overflow-y:auto; }
        .modal-footer { display:flex;justify-content:flex-end;gap:12px;padding:16px 24px;border-top:1px solid var(--cream-dark); }
      `}</style>
    </div>
  );
}
